import React, { useState, useEffect } from 'react';
import './MicroCredentials.css';
import CredentialGeneratorModal from './CredentialGeneratorModal';
import CredentialDetailsModal from './CredentialDetailsModal';
import { getSelectedCredentials, deleteSelectedCredentials } from '../services/api';
import { useNotification, NotificationContainer } from './Notification';

const MicroCredentials = ({ school = 'MARYLAND', programName, courseName }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState(null);
    const [microCredentials, setMicroCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingCredential, setDeletingCredential] = useState(null);
  
  // Use existing notification system
  const { notifications, addNotification, removeNotification } = useNotification();
  
  // Badge rotation sequence: purple, green, pink, yellow
  const badgeSequence = [
    "/Badge-purple.svg",
    "/Badge-green.svg", 
    "/Badge-pink.svg",
    "/Badge-yellow.svg"
  ];

  // Fetch credentials data from API
  const fetchCredentials = async () => {
    if (!school) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getSelectedCredentials(school, programName, courseName);
      
      // Transform API response to component format
      // Expected response: { "credential_name": ["course_code1", "course_code2"] }
      const credentialsArray = Object.entries(response).map(([credentialName, courseCodes], index) => ({
        id: index + 1,
        name: credentialName,
        courseCount: Array.isArray(courseCodes) ? courseCodes.length : 0,
        badgeImage: badgeSequence[index % badgeSequence.length], // Rotate through badges
        courseCodes: Array.isArray(courseCodes) ? courseCodes : []
      }));
      
      setMicroCredentials(credentialsArray);
    } catch (err) {
      console.error('Error fetching credentials:', err);
      setError('Failed to load micro-credentials');
      // Fallback to empty array on error
      setMicroCredentials([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch credentials data from API on component mount
  useEffect(() => {
    fetchCredentials();
  }, [school, programName, courseName]);

  const handleCreateNew = () => {
    setShowModal(true);
  };

  const handleViewDetails = (credentialId) => {
    // Find the credential by ID
    const credential = microCredentials.find(cred => cred.id === credentialId);
    if (credential) {
      setSelectedCredential(credential);
      setShowDetailsModal(true);
    }
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedCredential(null);
  };

  const handleDeleteCredential = async (credentialName) => {
    try {
      setDeletingCredential(credentialName);
      const response = await deleteSelectedCredentials(school, credentialName);
      console.log('Credential deleted successfully:', response);
      // Refresh the credentials list after deletion
      fetchCredentials();
      // Show notification using existing system
      addNotification('Credential Deleted', 'success');
    } catch (error) {
      console.error('Error deleting credential:', error);
      addNotification('Failed to delete credential', 'error');
    } finally {
      setDeletingCredential(null);
    }
  };

  if (loading) {
    return (
      <div className="microcredentials-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading micro-credentials...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="microcredentials-container">
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="microcredentials-container">
      <div className="microcredentials-grid">
        {/* Create New Card */}
        <div className="microcredential-card create-new-card" onClick={handleCreateNew}>
          <div className="create-new-content">
            <div className="create-icon-wrapper">
              <div className="create-icon">
                <svg width="39" height="39" viewBox="0 0 39 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.5 8.125V30.875M8.125 19.5H30.875" stroke="#2E2FB9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
            <h3 className="create-title">Create a New Micro-credential</h3>
          </div>
        </div>

        {/* Display fetched micro-credentials */}
        {microCredentials.length === 0 ? (
          <div className="no-credentials-message">
            <p>No micro-credentials found for this school.</p>
          </div>
        ) : (
          microCredentials.map((credential) => (
            <div key={credential.id} className="microcredential-card">
              {/* Delete Button */}
              <button 
                className={`delete-credential-btn ${deletingCredential === credential.name ? 'deleting' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (deletingCredential !== credential.name) {
                    handleDeleteCredential(credential.name);
                  }
                }}
                title={`Delete ${credential.name}`}
                disabled={deletingCredential === credential.name}
              >
                {deletingCredential === credential.name ? (
                  <div className="delete-loading-spinner"></div>
                ) : (
                  <img 
                    src="/delete.svg" 
                    alt="Delete credential" 
                    className="delete-icon"
                    width="16" 
                    height="16"
                  />
                )}
              </button>
              
              <div className="card-content">
                <div className="badge-container" onClick={() => handleViewDetails(credential.id)}>
                  <img 
                    src={credential.badgeImage} 
                    alt={`${credential.name} Badge`}
                    className="badge-image"
                  />
                </div>
                
                <div className="bottom-section">
                  <div className="credential-info">
                    <h3 className="credential-name">{credential.name}</h3>
                    <p className="credential-courses">
                      {credential.courseCount} Course{credential.courseCount !== 1 ? 's' : ''} Related
                    </p>
                  </div>
                  
                  <button 
                    className="view-details-btn"
                    onClick={() => handleViewDetails(credential.id)}
                  >
                    <span>View Details</span>
                    <svg width="12" height="24" viewBox="0 0 12 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.08997 5.79004L8.29997 11L3.08997 16.21" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      <CredentialGeneratorModal 
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onCredentialsGenerated={fetchCredentials}
        programName={programName}
      />
      
      <CredentialDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
        credential={selectedCredential}
        programName={programName}
        school={school}
        onDeleteCredential={handleDeleteCredential}
      />
      
      {/* Notification Container for displaying notifications */}
      <NotificationContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
    </div>
  );
};

export default MicroCredentials;
